import type { ComponentProps } from "react";
import { createContext, use } from "react";

import { useIsMobile } from "../hooks/use-mobile";
import { cn } from "../lib/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./drawer";

const ResponsiveDialogContext = createContext(false);

function ResponsiveDialog({
  children,
  ...props
}: ComponentProps<typeof Dialog>) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <ResponsiveDialogContext value={true}>
        <Drawer {...props}>{children}</Drawer>
      </ResponsiveDialogContext>
    );
  }

  return (
    <ResponsiveDialogContext value={false}>
      <Dialog {...props}>{children}</Dialog>
    </ResponsiveDialogContext>
  );
}

function ResponsiveDialogTrigger({
  ...props
}: ComponentProps<typeof DialogTrigger>) {
  const isMobile = use(ResponsiveDialogContext);
  return isMobile ? <DrawerTrigger {...props} /> : <DialogTrigger {...props} />;
}

function ResponsiveDialogContent({
  className,
  ...props
}: ComponentProps<typeof DialogContent>) {
  const isMobile = use(ResponsiveDialogContext);
  return isMobile ? (
    <DrawerContent
      className={cn("px-4 pb-4 max-h-[90vh] overflow-hidden", className)}
      {...props}
    />
  ) : (
    <DialogContent className={className} {...props} />
  );
}

function ResponsiveDialogHeader({
  className,
  ...props
}: ComponentProps<typeof DialogHeader>) {
  const isMobile = use(ResponsiveDialogContext);
  return isMobile ? (
    <DrawerHeader className={cn("px-0", className)} {...props} />
  ) : (
    <DialogHeader className={className} {...props} />
  );
}

function ResponsiveDialogTitle({
  ...props
}: ComponentProps<typeof DialogTitle>) {
  const isMobile = use(ResponsiveDialogContext);
  return isMobile ? <DrawerTitle {...props} /> : <DialogTitle {...props} />;
}

function ResponsiveDialogDescription({
  ...props
}: ComponentProps<typeof DialogDescription>) {
  const isMobile = use(ResponsiveDialogContext);
  return isMobile ? (
    <DrawerDescription {...props} />
  ) : (
    <DialogDescription {...props} />
  );
}

function ResponsiveDialogFooter({
  className,
  ...props
}: ComponentProps<typeof DialogFooter>) {
  const isMobile = use(ResponsiveDialogContext);
  return isMobile ? (
    <DrawerFooter className={cn("px-0", className)} {...props} />
  ) : (
    <DialogFooter className={className} {...props} />
  );
}

function ResponsiveDialogClose({
  ...props
}: ComponentProps<typeof DialogClose>) {
  const isMobile = use(ResponsiveDialogContext);
  return isMobile ? <DrawerClose {...props} /> : <DialogClose {...props} />;
}

export {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
};
